import { useInfiniteQuery } from '@tanstack/react-query';

import { http } from '@/shared/api/http';
import type { DeepRequired, Schema, WithOptional } from '@/shared/api';

/** Категории документов задачи (значения бэка). */
export type TaskDocCategory = 'tz' | 'contract' | 'other' | 'checklists';

export type TaskDocument = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.TaskDocument'>>,
  'downloadUrl' | 'mimeType' | 'fileSize' | 'createdAt'
>;

export type TaskDocumentsPage = DeepRequired<
  Schema<'internal_handler_http.TaskDocumentsResponse'>
>;

const PAGE_LIMIT = 20;

export async function fetchTaskDocuments(
  taskId: string | number,
  params: {
    category?: TaskDocCategory;
    q?: string;
    limit?: number;
    offset?: number;
  }
): Promise<TaskDocumentsPage> {
  const { category, q, limit = PAGE_LIMIT, offset = 0 } = params;
  const { data } = await http.get<TaskDocumentsPage>(
    `/tasks/${taskId}/documents`,
    { params: { category, q: q || undefined, limit, offset } }
  );
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    total: Number(data?.total ?? 0),
    limit: Number(data?.limit ?? limit),
    offset: Number(data?.offset ?? offset),
  };
}

/** Ключ кэша списка документов задачи (для инвалидации после загрузки). */
export function taskDocumentsQueryKey(taskId: string | number) {
  return ['task-documents', String(taskId)] as const;
}

/** Список документов задачи по категории с серверным поиском и пагинацией. */
export function useTaskDocuments(
  taskId: string | number | undefined,
  category: TaskDocCategory | undefined,
  q: string,
  enabled: boolean
) {
  return useInfiniteQuery({
    queryKey: [...taskDocumentsQueryKey(taskId ?? ''), category ?? 'all', q],
    queryFn: ({ pageParam }) =>
      fetchTaskDocuments(taskId as string, {
        category,
        q,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.limit;
      return next < last.total ? next : undefined;
    },
    enabled: enabled && Boolean(taskId),
    staleTime: 30_000,
  });
}
